import React, { useCallback, useEffect, useState } from 'react';
import Button from './Button';
import TextInput from './TextInput';
import RichTextInput from './RichTextInput';
import ImageUpload from './ImageUpload';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { Badge } from './Badge';
import { cn } from '../lib/utils';
import { DEFAULT_MODEL_ID, MODELS } from '../constants';
import { 
  FileText, 
  Image as ImageIcon, 
  Type, 
  Upload, 
  Trash2, 
  Bot, 
  CheckCircle,
  AlertCircle,
  FileUp
} from 'lucide-react';

interface ImageData {
  id?: string;
  pageLabel: string;
  base64?: string;
  previewUrl?: string;
  file?: File;
}

interface EssayInput {
  type: 'images' | 'document' | 'text';
  data: ImageData[] | File[] | string;
}

interface EssayDetailsFormProps {
  assessmentName: string;
  initialStudentName?: string;
  initialImages?: ImageData[];
  onSubmit: (studentName: string, essayInput: EssayInput, selectedModelIds: string[]) => void;
  onBack: () => void;
  isProcessing?: boolean;
  defaultSelectedModelIds?: string[];
  isAdmin?: boolean;
  initialInputMethod?: 'image' | 'document' | 'text';
  lockInputMethod?: boolean;
}

// A component to render a list of selected document files.
const DocumentList = ({ files, onRemove, disabled }: { files: File[], onRemove: (file: File) => void, disabled: boolean }) => {
  if (!files || files.length === 0) return null;

  return (
    <div className="space-y-3 mt-4">
      <h4 className="text-sm font-semibold text-foreground">Selected Documents:</h4>
      {files.map((file) => (
        <div key={`${file.name}-${file.size}`} className="flex items-center justify-between gap-4 p-4 border border-border rounded-lg bg-card shadow-sm">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-grow min-w-0">
              <p className="text-sm font-medium text-foreground truncate" title={file.name}>{file.name}</p>
              <p className="text-xs text-muted-foreground">({(file.size / 1024 / 1024).toFixed(2)} MB)</p>
            </div>
          </div>
          <Button onClick={() => onRemove(file)} variant="destructive" size="sm" aria-label={`Remove ${file.name}`} disabled={disabled}>
            <Trash2 className="h-3 w-3" />
            Remove
          </Button>
        </div>
      ))}
    </div>
  );
};

const EssayDetailsForm: React.FC<EssayDetailsFormProps> = ({
  assessmentName,
  initialStudentName = '',
  initialImages = [],
  onSubmit,
  onBack,
  isProcessing = false,
  defaultSelectedModelIds = [DEFAULT_MODEL_ID],
  isAdmin = false,
  initialInputMethod = 'image',
  lockInputMethod = false,
}) => {
  const [studentName, setStudentName] = useState(initialStudentName);
  const [uploadedImages, setUploadedImages] = useState<ImageData[]>(initialImages);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [essayText, setEssayText] = useState(''); // Will now hold HTML content
  const [inputMethod, setInputMethod] = useState<'image' | 'document' | 'text'>(initialInputMethod);
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>(defaultSelectedModelIds);
  const [errors, setErrors] = useState<{ studentName: string | null; content: string | null }>({ studentName: null, content: null });

  useEffect(() => {
    setInputMethod(initialInputMethod);
  }, [initialInputMethod]);

  const handleImagesConfirmed = useCallback((images: any[]) => {
    setUploadedImages(images as ImageData[]);
    if (images.length > 0) {
      setErrors(prev => ({ ...prev, content: null }));
    }
  }, []);
  
  const handleDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files ?? []);
    if (newFiles.length > 0) {
      let combinedFiles = [...documentFiles];
      let errorFound = false;

      newFiles.forEach(file => {
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          setErrors(prev => ({...prev, content: `File "${file.name}" is too large. Maximum size is 10MB.`}));
          errorFound = true;
          return; // Skip this file
        }
        // Prevent adding duplicates
        if (!combinedFiles.some(f => f.name === file.name && f.size === file.size)) {
          combinedFiles.push(file);
        }
      });

      if (!errorFound) {
        setErrors(prev => ({...prev, content: null}));
      }
      setDocumentFiles(combinedFiles);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleRemoveDocument = (fileToRemove: File) => {
    setDocumentFiles(prev => prev.filter(file => file !== fileToRemove));
  };


  const getPlainText = (html: string): string => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // First, find and remove all warning wrappers for remote images so their
    // content is not included in the final essay text.
    tempDiv.querySelectorAll('.remote-image-warning-wrapper').forEach(el => el.remove());
    
    // Replace block elements with newlines for better formatting
    tempDiv.querySelectorAll('div, p, br').forEach(el => {
      // Ensure the element wasn't part of a removed wrapper
      if(tempDiv.contains(el)) {
        el.appendChild(document.createTextNode('\n'));
      }
    });
    return tempDiv.textContent || "";
  };
  
  const handleTextChange = (htmlContent: string) => {
    setEssayText(htmlContent);
    if (getPlainText(htmlContent).trim() !== '') {
        setErrors(prev => ({ ...prev, content: null }));
    }
  };

  const handleSubmit = () => {
    const newErrors: { studentName: string | null; content: string | null } = { studentName: null, content: null };
    let hasError = false;

    if (studentName.trim() === '') {
      newErrors.studentName = "Please enter the student's name.";
      hasError = true;
    }

    if (isAdmin && selectedModelIds.length === 0) {
      newErrors.content = "Please select at least one AI model for marking.";
      hasError = true;
    }

    let essayInput: EssayInput;

    switch (inputMethod) {
      case 'image':
        if (uploadedImages.length === 0) {
          newErrors.content = "Please upload at least one essay page image.";
          hasError = true;
        } else {
          essayInput = { type: 'images', data: uploadedImages };
        }
        break;
      case 'document':
        if (documentFiles.length === 0) {
          newErrors.content = "Please upload at least one document file.";
          hasError = true;
        } else {
          essayInput = { type: 'document', data: documentFiles };
        }
        break;
      case 'text':
        const plainText = getPlainText(essayText);
        if (plainText.trim() === '') {
          newErrors.content = "Please paste the essay text.";
          hasError = true;
        } else {
          essayInput = { type: 'text', data: plainText.trim() };
        }
        break;
      default:
        return; // This should never happen
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({ studentName: null, content: null });
    const modelsForSubmit = isAdmin ? selectedModelIds : [DEFAULT_MODEL_ID];
    onSubmit(studentName, essayInput!, modelsForSubmit);
  };

  const canSubmit = studentName.trim() !== '' && (isAdmin ? selectedModelIds.length > 0 : true) && !isProcessing && (
    (inputMethod === 'image' && uploadedImages.length > 0) ||
    (inputMethod === 'document' && documentFiles.length > 0) ||
    (inputMethod === 'text' && getPlainText(essayText).trim() !== '')
  );

  const tabs = [
    { id: 'image' as const, label: 'Upload Images', icon: ImageIcon },
    { id: 'document' as const, label: 'Upload Document', icon: FileUp },
    { id: 'text' as const, label: 'Paste Text & Images', icon: Type },
  ];

  return (
    <Card className="w-full">
      {/* Tab Navigation */}
      {!lockInputMethod && (
        <div className="px-6 pt-6">
          <div className="border-b border-border">
            <nav className="-mb-px flex space-x-6" aria-label="Tabs">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = inputMethod === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setInputMethod(tab.id)}
                    className={cn(
                      "flex items-center gap-2 whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors",
                      isActive
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                    )}
                    aria-current={isActive ? 'page' : undefined}
                    disabled={isProcessing}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      <CardContent className="p-6">
        {/* Upload Section */}
        {inputMethod === 'image' && (
          <ImageUpload
            initialImages={uploadedImages}
            onImagesConfirmed={handleImagesConfirmed}
            isLoading={isProcessing}
          />
        )}
        
        {inputMethod === 'document' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Upload a DOCX or PDF file containing this essay.</p>
            <label htmlFor="doc-upload" className="inline-block cursor-pointer">
              <input
                id="doc-upload"
                type="file"
                multiple
                className="sr-only"
                onChange={handleDocumentChange}
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,application/pdf"
                disabled={isProcessing}
              />
              <span className="inline-flex items-center gap-2 bg-background border border-input text-foreground hover:bg-accent hover:text-accent-foreground font-medium rounded-lg shadow-sm px-4 py-2.5 text-sm transition-colors">
                <Upload className="h-4 w-4" />
                Choose document
              </span>
            </label>
            <DocumentList files={documentFiles} onRemove={handleRemoveDocument} disabled={isProcessing} />
          </div>
        )}
        
        {inputMethod === 'text' && (
          <RichTextInput
            label="Essay Content"
            id="essayText"
            value={essayText}
            onChange={handleTextChange}
            placeholder="Paste the full text of the student's essay here. You can also paste images directly into this box."
            disabled={isProcessing}
            error={errors.content}
          />
        )}
        
        {errors.content && (
          <div className="mt-3 flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {errors.content}
          </div>
        )}
        
        {/* Student Info & Model Selection */}
        <div className="pt-8 mt-8 border-t border-border space-y-6">
          <div className="max-w-md">
            <TextInput
              label="Student Name"
              id="studentName"
              value={studentName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setStudentName(e.target.value)
                if (e.target.value.trim() !== '') {
                  setErrors(prev => ({...prev, studentName: null}));
                }
              }}
              placeholder="e.g., John Doe"
              required
              error={errors.studentName ?? undefined}
            />
          </div>

          {/* AI Model Selection Section */}
          {isAdmin && (
            <div className="pt-6 border-t border-border">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-foreground">Select AI Models for Marking</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose which AI models you want to use for marking this essay. You can select multiple models for comparison.
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.values(MODELS).map((model) => {
                  const isSelected = selectedModelIds.includes(model.id);
                  return (
                    <label
                      key={model.id}
                      className={cn(
                        "relative flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all duration-200",
                        isSelected
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border bg-card hover:border-primary/30 hover:shadow-sm",
                        isProcessing && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedModelIds([...selectedModelIds, model.id]);
                          } else {
                            setSelectedModelIds(selectedModelIds.filter(id => id !== model.id));
                          }
                          // Clear content error when a model is selected
                          if (selectedModelIds.length >= 0) {
                            setErrors(prev => ({ ...prev, content: null }));
                          }
                        }}
                        disabled={isProcessing}
                        className="h-4 w-4 rounded border-input text-primary focus:ring-primary/50 mt-1 flex-shrink-0"
                      />
                      <div className="ml-3 min-w-0">
                        <div className="flex items-center gap-2">
                          <Bot className="h-4 w-4 text-primary" />
                          <span className="font-medium text-foreground">{model.name}</span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">{model.description}</div>
                      </div>
                      {isSelected && (
                        <CheckCircle className="absolute top-3 right-3 h-5 w-5 text-primary" />
                      )}
                    </label>
                  );
                })}
              </div>
              
              {selectedModelIds.length === 0 && (
                <div className="mt-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  Please select at least one AI model for marking.
                </div>
              )}
              
              <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-foreground">
                  <strong>Selected:</strong>{' '}
                  {selectedModelIds.length > 0 
                    ? selectedModelIds.map(id => (
                        <Badge key={id} variant="secondary" className="mr-1">
                          {MODELS[id].name}
                        </Badge>
                      ))
                    : <span className="text-muted-foreground">None</span>
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      
      {/* Submit Button */}
      <div className="flex justify-end px-6 pb-6">
        <Button onClick={handleSubmit} disabled={!canSubmit} isLoading={isProcessing} size="lg">
          <CheckCircle className="h-4 w-4" />
          Mark Essay & Generate Feedback
        </Button>
      </div>
    </Card>
  );
};

export default EssayDetailsForm;
